import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRightIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'

import { isGroupActive } from './navigation'

/**
 * One sidebar entry — VH-133
 *
 * A leaf renders as a link. An item with children renders as a link *and* a
 * separate expand control, which is the important detail: the parent
 * destinations (Workouts, Nutrition, My Progress) are real screens, so making
 * the whole row a toggle would strand them. Expanding and navigating are two
 * different intents and get two different targets.
 *
 * The group opens itself when the route is inside it — including on a cold
 * load of a deep link, where the user would otherwise see a collapsed sidebar
 * with nothing marked active and no clue where they are.
 */
export default function NavGroup({ item, pathname, onNavigate }) {
  const active = item.href === pathname
  const containsRoute = isGroupActive(item, pathname)
  const [expanded, setExpanded] = useState(containsRoute)

  // Re-open on navigation into the group. Deliberately one-way: it does not
  // collapse when you navigate away, because a group snapping shut under the
  // pointer is disorienting and the user may be moving between its children.
  useEffect(() => {
    if (containsRoute) setExpanded(true)
  }, [containsRoute])

  const rowClass = (isActive, indented) =>
    clsx(
      'group flex items-center py-2 text-sm font-medium border-l-4 rounded-r-md transition-colors',
      // Links get no focus ring from the browser once the outline is styled
      // away by the reset, and a keyboard user navigating the sidebar then has
      // no idea where they are. `focus-visible` so it appears for the keyboard
      // without ringing every mouse click.
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
      isActive
        ? 'bg-primary-50 border-primary-500 text-primary-700'
        : 'border-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900',
      indented ? 'pl-8 pr-3' : 'px-3'
    )

  if (!item.children) {
    return (
      <Link to={item.href} className={rowClass(active, false)} onClick={onNavigate}>
        <item.icon className="mr-3 flex-shrink-0 h-5 w-5" />
        {item.name}
      </Link>
    )
  }

  return (
    <div>
      <div className="flex items-stretch">
        <Link
          to={item.href}
          className={clsx(rowClass(active, false), 'flex-1 min-w-0')}
          onClick={onNavigate}
        >
          <item.icon className="mr-3 flex-shrink-0 h-5 w-5" />
          <span className="truncate">{item.name}</span>
        </Link>
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-controls={`nav-group-${item.key}`}
          aria-label={item.name}
          className="px-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <ChevronRightIcon
            className={clsx(
              'h-4 w-4 transition-transform',
              expanded && 'rotate-90'
            )}
          />
        </button>
      </div>

      {/* Unmounted rather than hidden when collapsed, so its links are out of
          the tab order — a keyboard user should not tab through items they
          cannot see. */}
      {expanded && (
        <div id={`nav-group-${item.key}`} className="mt-1 space-y-1">
          {item.children.map((child) => (
            <Link
              key={child.key}
              to={child.href}
              className={rowClass(child.href === pathname, true)}
              onClick={onNavigate}
            >
              <child.icon className="mr-3 flex-shrink-0 h-4 w-4" />
              {child.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
